/**
 * Form Assembly Submission Tracking
 *
 * Watches for Form Assembly form completions via MutationObserver
 * and pushes a dataLayer event for GTM conversion tracking.
 *
 * Form Assembly renders a `.wFormThankYou` element when a form is
 * successfully submitted. This script fires a custom `form_submission`
 * event for that element, used as a GTM trigger for Google Ads
 * conversion tracking.
 *
 * REST-embedded forms submit to Form Assembly and return to the page
 * with `tfa_next`, so the thank-you element is usually server-rendered
 * and already present at load. Elements added later are still caught
 * by the MutationObserver. Each element is reported once.
 *
 * @since 2.0.0
 * @package Playworks
 */

(function() {
	'use strict';

	window.dataLayer = window.dataLayer || [];

	var reported = [];

	/**
	 * Get the Form Assembly form ID from the closest formassembly container
	 *
	 * @param {Element} thankYouEl The .wFormThankYou element.
	 * @returns {string} The form ID or 'unknown'.
	 */
	function getFormId( thankYouEl ) {
		var container = thankYouEl.closest( '.formassembly--custom' ) || thankYouEl.closest( '[data-formid]' );
		if ( container && container.getAttribute( 'data-formid' ) ) {
			return container.getAttribute( 'data-formid' );
		}
		return 'unknown';
	}

	/**
	 * Get campaign identifier from URL parameters
	 *
	 * @returns {string} The utm_campaign parameter value or page slug.
	 */
	function getCampaign() {
		var params = new URLSearchParams( window.location.search );
		return params.get( 'utm_campaign' ) || window.location.pathname.replace( /\//g, '' ) || 'landing-page';
	}

	/**
	 * Push a `form_submission` event for a thank-you element
	 *
	 * Skips elements already reported, so a re-render or a second
	 * mutation for the same node does not double count.
	 *
	 * @param {Element} thankYouEl The .wFormThankYou element.
	 */
	function reportSubmission( thankYouEl ) {
		if ( reported.indexOf( thankYouEl ) !== -1 ) {
			return;
		}
		reported.push( thankYouEl );

		window.dataLayer.push({
			event: 'form_submission',
			form_id: getFormId( thankYouEl ),
			campaign: getCampaign(),
			page_path: window.location.pathname
		});
	}

	/**
	 * Report thank-you elements present at load, then watch for new ones
	 */
	function init() {
		var existing = document.querySelectorAll( '.wFormThankYou' );
		for ( var j = 0; j < existing.length; j++ ) {
			reportSubmission( existing[j] );
		}

		var observer = new MutationObserver( function( mutations ) {
			mutations.forEach( function( mutation ) {
				if ( mutation.type !== 'childList' ) {
					return;
				}

				var addedNodes = mutation.addedNodes;
				for ( var i = 0; i < addedNodes.length; i++ ) {
					var node = addedNodes[i];
					if ( node.nodeType !== 1 ) {
						continue;
					}

					// Check if the added node is or contains .wFormThankYou
					var thankYouEl = null;
					if ( node.classList && node.classList.contains( 'wFormThankYou' ) ) {
						thankYouEl = node;
					} else if ( node.querySelector ) {
						thankYouEl = node.querySelector( '.wFormThankYou' );
					}

					if ( thankYouEl ) {
						reportSubmission( thankYouEl );
					}
				}
			});
		});

		observer.observe( document.body, {
			childList: true,
			subtree: true
		});
	}

	// Initialize when DOM is ready
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
})();
